import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  skip,
  startWith,
  Subscription,
  tap,
} from 'rxjs';
import {
  EGenericInputTriggerType,
  GenericInputStatusEnum,
} from '../../../models/irembo-generic-input-status.enum';
import { IEnvironment } from '../../../models/environment.model';
import { v4 as uuidv4 } from 'uuid';
import {
  checkForValidFields,
  populateFormFields,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
  findFormControl,
  findFormControlInScope,
  handleErrorMapping,
  resetFieldsToPopulate,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import {
  FormlyFieldConfig,
  FormlyValueChangeEvent,
} from '@ngx-formly/core/lib/models';
import { AbstractControl } from '@angular/forms';
import { getParentRepeaterKeyAndItemIndex } from '../../../../utils/utils/formly.utils';
import {
  TransformToArrayConfig,
  TransformFieldMapping,
} from '../../../models/multi-select-data-fetch.model';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';
import {
  INidVerificationConfig,
  IPreFetchCheckConfig,
  IIntegrationFetchSyncRequest,
  IIntegrationFetchSyncResponse,
} from '../../../models/generic-data-fetch-pre-pipeline.model';
import {
  EIdType,
  ENIDType,
  TNIDType,
} from '../../../config/custom-id-input-api-urls.config';
import {
  NidVerificationComponent,
  ENidVerificationErrorKeys,
} from '../../nid-verification/nid-verification.component';
import { PreFetchCheckService } from '../../../services/pre-fetch-check/pre-fetch-check.service';

export interface ListenFieldConfig {
  key: string;
  mapTo?: string;
}

@Component({
  standalone: false,
  selector: 'irembogov-custom-generic-guarded-data-fetch',
  templateUrl: './custom-generic-guarded-data-fetch.component.html',
  styleUrls: ['./custom-generic-guarded-data-fetch.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  encapsulation: ViewEncapsulation.None,
})
export class CustomGenericGuardedDataFetchComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, AfterViewInit
{
  protected subscriptions = new Subscription();
  statusClass: string | undefined;
  fieldsToPopulate: Record<string, string>[] = [];
  protected environment: IEnvironment;
  private isInRepeater = false;
  private currentRepeaterForm: any;
  protected componentId: string;
  private isCurrentlyFetching = false;

  /** Resolved NID sub-type from the form */
  private resolvedNidType?: TNIDType;

  @ViewChild('nidVerification')
  private nidVerificationComponent!: NidVerificationComponent;

  constructor(
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    protected formStateService: FormStateService,
    private preFetchCheckService: PreFetchCheckService,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
    this.componentId = uuidv4();
  }

  // ============================================================
  // Repeater / form control utilities
  // ============================================================

  protected initializeRepeaterContext() {
    const repeaterKeys = getParentRepeaterKeyAndItemIndex(this.field);
    if (repeaterKeys) {
      this.isInRepeater = true;
      this.currentRepeaterForm = this.form;
    } else {
      this.isInRepeater = false;
      this.currentRepeaterForm = getPopulateReferenceForm(
        this.field,
        this.form
      );
    }
  }

  private isThisExactField(changeField: any): boolean {
    if (!this.isInRepeater) {
      return changeField.key === this.field.key;
    }
    const getFieldPath = (field: any): string => {
      const path: string[] = [];
      let current = field;
      while (current) {
        path.unshift(current.key);
        current = current.parent;
      }
      return path.join('.');
    };
    return getFieldPath(changeField) === getFieldPath(this.field);
  }

  protected findControl(
    targetKey: string,
    searchInRootForm?: boolean
  ): AbstractControl | null {
    if (searchInRootForm === true) {
      return findFormControl(this.currentRepeaterForm, targetKey);
    }
    if (searchInRootForm === false && this.isInRepeater) {
      return findFormControlInScope(this.currentRepeaterForm, targetKey);
    }
    return this.isInRepeater
      ? findFormControlInScope(this.currentRepeaterForm, targetKey)
      : findFormControl(this.currentRepeaterForm, targetKey);
  }

  // ============================================================
  // Field listeners
  // ============================================================

  setupFieldListeners() {
    const { payloadKeyMappings, useMultiplePayloadKeys, payloadKey } =
      this.field.props;
    const debounceMs = this.field.props['debounceTime'] || 500;
    const disableFirstFetch = this.field.props['disableFirstFetchOnRFA'];
    const isInRFAState = this.formStateService.checkIfApplicationInAnRFAState();

    if (
      useMultiplePayloadKeys &&
      !(Array.isArray(payloadKeyMappings) || payloadKeyMappings.length)
    )
      return;

    if (!useMultiplePayloadKeys) {
      const control = this.findControl(payloadKey);
      if (control) {
        let stream = control.valueChanges.pipe(
          startWith(control.value),
          distinctUntilChanged(),
          debounceTime(debounceMs),
          tap(() => this.formStateService.clearGlobalError(this.field))
        );
        if (disableFirstFetch && isInRFAState) {
          stream = stream.pipe(skip(1));
        }
        this.subscriptions.add(
          stream.subscribe(value => {
            if (value !== null && value !== undefined) {
              this.onDataFetch(value);
            }
          })
        );
        return;
      }
    }

    const fieldControls: AbstractControl[] = [];
    const fieldConfigs: any[] = [];
    payloadKeyMappings.forEach((mapping: any) => {
      if (!mapping.isStatic && mapping.sourceField) {
        const control = this.findControl(
          mapping.sourceField,
          mapping.searchInRootForm
        );
        if (control) {
          fieldControls.push(control);
          fieldConfigs.push(mapping);
        }
      }
    });

    if (!fieldControls.length) return;

    let fieldObservables;
    if (isInRFAState) {
      fieldObservables = fieldControls.map(control => {
        let stream = control.valueChanges.pipe(
          startWith(control.value),
          distinctUntilChanged(),
          debounceTime(debounceMs)
        );
        if (disableFirstFetch) {
          stream = stream.pipe(skip(1));
        }
        return stream;
      });
    } else {
      fieldObservables = fieldControls.map(control => {
        let stream = control.valueChanges.pipe(
          control.value != null && control.value !== ''
            ? startWith(control.value)
            : distinctUntilChanged(),
          debounceTime(debounceMs)
        );
        if (disableFirstFetch) {
          stream = control.valueChanges.pipe(
            startWith(control.value),
            skip(1),
            distinctUntilChanged(),
            debounceTime(debounceMs)
          );
        }
        return stream;
      });
    }

    this.subscriptions.add(
      combineLatest(fieldObservables)
        .pipe(tap(() => this.formStateService.clearGlobalError(this.field)))
        .subscribe(values => {
          if (values.every(v => v !== null && v !== undefined)) {
            const firstValue = this.findControl(
              fieldConfigs[0].sourceField,
              fieldConfigs[0].searchInRootForm
            )?.value;
            this.onDataFetch(firstValue);
          }
        })
    );
  }

  // ============================================================
  // Lifecycle
  // ============================================================

  ngAfterViewInit(): void {
    this.initializeRepeaterContext();

    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        this.currentRepeaterForm
      )
    );

    if (
      this.field.props['triggerType'] ===
      EGenericInputTriggerType.FIELD_LISTENER
    ) {
      this.setupFieldListeners();
    }

    this.subscriptions.add(
      this.field.options?.fieldChanges
        ?.asObservable()
        .pipe(
          filter((change: FormlyValueChangeEvent) => {
            if (
              this.isThisExactField(change.field) &&
              change.type === 'valueChanges' &&
              this.field.props['triggerType'] ===
                EGenericInputTriggerType.TRIGGER_BUTTON &&
              change.value
            ) {
              this.formControl.setErrors({
                ...this.formControl.errors,
                notVerifiedByTriggerAction: true,
              });
            }
            return (
              this.isThisExactField(change.field) &&
              change.type === 'expressionChanges' &&
              change['property'] === 'props.runningAction'
            );
          }),
          distinctUntilChanged(
            (a: FormlyValueChangeEvent, b: FormlyValueChangeEvent) =>
              a.value === b.value
          )
        )
        ?.subscribe((change: FormlyValueChangeEvent) => {
          if (
            change.value &&
            this.field.formControl.value &&
            !this.isCurrentlyFetching
          ) {
            this.onDataFetch(this.field.formControl.value);
            return;
          }
          this.field.props['runAction'] = false;
        })
    );

    checkForValidFields(this.field);

    this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    if (this.fieldsToPopulate?.length > 0) {
      this.subscriptions.add(
        this.field.formControl.valueChanges
          .pipe(
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) === JSON.stringify(b)
            )
          )
          .subscribe({
            next: () => {
              if (this.field.formControl.invalid) {
                resetFieldsToPopulate(
                  this.fieldsToPopulate,
                  this.currentRepeaterForm,
                  this.formStateService
                );
              }
            },
          })
      );
    }

    this.setupNidTypeSourceListener();
    this.prefillFromNidProfile();
  }

  // ============================================================
  // NID type source resolution
  // ============================================================

  private setupNidTypeSourceListener(): void {
    const nidConfig: INidVerificationConfig | undefined =
      this.field.props?.['nidVerification'];

    if (!nidConfig?.enabled || !nidConfig.nidTypeSource) {
      return;
    }

    const sourceControl = this.findControl(nidConfig.nidTypeSource);
    if (!sourceControl) {
      return;
    }

    this.resolvedNidType = this.mapToNidType(sourceControl.value);

    this.subscriptions.add(
      sourceControl.valueChanges
        .pipe(distinctUntilChanged())
        .subscribe(value => {
          this.resolvedNidType = this.mapToNidType(value);
        })
    );
  }

  private mapToNidType(value: unknown): TNIDType | undefined {
    if (!value || typeof value !== 'string') return undefined;
    const normalized = value.toUpperCase();

    if (normalized === ENIDType.NATIONAL_ID || normalized === 'NID') {
      return ENIDType.NATIONAL_ID;
    }
    if (normalized === ENIDType.FOREIGNER_ID) {
      return ENIDType.FOREIGNER_ID;
    }
    if (normalized === ENIDType.REFUGEE_ID) {
      return ENIDType.REFUGEE_ID;
    }
    return undefined;
  }

  private prefillFromNidProfile(): void {
    const nidConfig: INidVerificationConfig | undefined =
      this.field.props?.['nidVerification'];

    if (
      !nidConfig?.enabled ||
      !nidConfig.usePrefetch ||
      nidConfig.idType !== EIdType.NID ||
      this.environment.applicationByOther ||
      this.formControl.value
    ) {
      return;
    }

    if (this.isRfaStateButFieldNotFlagged()) {
      return;
    }

    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.nidVerificationComponent
      .prefetchVerifiedIdNumber(nidConfig, this.resolvedNidType)
      .then(identificationNumber => {
        if (!identificationNumber) {
          this.statusClass = undefined;
          this.cd.detectChanges();
          return;
        }

        this.formControl.setValue(identificationNumber, { emitEvent: false });
        this.onDataFetch(identificationNumber);
      });
  }

  // In an RFA flow, only prefill fields the officer flagged for revision.
  private isRfaStateButFieldNotFlagged(): boolean {
    if (!this.formStateService.checkIfApplicationInAnRFAState()) {
      return false;
    }
    const flaggedFields =
      this.formStateService.getCurrentFormlyFormState()?.[
        EIremboFormlyFormStateKeys.RFA_FLAGGED_FIELDS
      ];
    return !flaggedFields?.includes(this.field.key);
  }

  // ============================================================
  // Payload builder
  // ============================================================

  buildPayload(inputValue: string): Record<string, unknown> {
    const {
      useMultiplePayloadKeys,
      payloadKey,
      payloadKeyMappings,
      triggerType,
    } = this.field.props;

    if (triggerType !== EGenericInputTriggerType.FIELD_LISTENER) {
      if (!useMultiplePayloadKeys || !payloadKeyMappings?.length) {
        return { [payloadKey]: inputValue };
      }
      const payload: Record<string, unknown> = {};
      payloadKeyMappings.forEach((mapping: any) => {
        if (mapping.isStatic) {
          payload[mapping.key] = mapping.staticValue;
        } else if (mapping.sourceField) {
          const sourceValue = this.findControl(
            mapping.sourceField,
            mapping.searchInRootForm
          )?.value;
          payload[mapping.key] = sourceValue;
        } else {
          payload[mapping.key] = inputValue;
        }
      });
      return payload;
    }

    if (!useMultiplePayloadKeys) {
      const control = this.findControl(payloadKey);
      return { [payloadKey]: control?.value };
    }

    const payload: Record<string, unknown> = {};
    payloadKeyMappings.forEach((mapping: any) => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        const control = this.findControl(
          mapping.sourceField,
          mapping.searchInRootForm
        );
        payload[mapping.key] = control?.value;
      }
    });
    return payload;
  }

  // ============================================================
  // Pipeline entry point
  // ============================================================

  onDataFetch(inputValue: string) {
    if (this.isCurrentlyFetching) {
      return;
    }

    const nidConfig: INidVerificationConfig | undefined =
      this.field.props?.['nidVerification'];
    const preFetchChecks: IPreFetchCheckConfig[] =
      this.field.props?.['preFetchChecks'] ?? [];
    const enabledChecks = preFetchChecks.filter(check => check.enabled);
    const hasNidVerification = nidConfig?.enabled === true;

    if (!hasNidVerification && enabledChecks.length === 0) {
      this.executeMainDataFetch(inputValue);
      return;
    }

    this.clearPipelineErrors();

    this.isCurrentlyFetching = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.cd.detectChanges();

    this.runPipeline(inputValue, nidConfig, enabledChecks)
      .then(passed => {
        if (passed) {
          this.isCurrentlyFetching = false;
          this.executeMainDataFetch(inputValue);
        } else {
          this.isCurrentlyFetching = false;
          if (
            this.field.props?.['triggerType'] ===
            EGenericInputTriggerType.TRIGGER_BUTTON
          ) {
            this.field.props['runAction'] = false;
          }
          this.cd.detectChanges();
        }
      })
      .catch(() => {
        this.isCurrentlyFetching = false;
        this.statusClass = GenericInputStatusEnum.DANGER;
        if (
          this.field.props?.['triggerType'] ===
          EGenericInputTriggerType.TRIGGER_BUTTON
        ) {
          this.field.props['runAction'] = false;
        }
        this.cd.detectChanges();
      });
  }

  private async runPipeline(
    inputValue: string,
    nidConfig: INidVerificationConfig | undefined,
    enabledChecks: IPreFetchCheckConfig[]
  ): Promise<boolean> {
    // Step 1: NID verification
    if (nidConfig?.enabled) {
      const result = await this.nidVerificationComponent.verify(
        nidConfig,
        inputValue,
        this.resolvedNidType
      );

      if (!result.passed) {
        this.setPipelineError(
          result.errorKey ?? ENidVerificationErrorKeys.VERIFICATION_FAILED,
          result.errorMessage
        );
        return false;
      }

      // Prefetch may have overridden the citizenId
      if (result.citizenId) {
        inputValue = result.citizenId;
        this.formControl.setValue(inputValue);
      }
    }

    // Step 2: Pre-fetch checks
    if (enabledChecks.length > 0) {
      const result = await this.preFetchCheckService.executeChecks(
        enabledChecks,
        inputValue,
        (sourceField, searchInRootForm) =>
          this.findControl(sourceField, searchInRootForm)?.value,
        this.environment
      );

      if (!result.passed) {
        this.setPipelineError(
          result.errorKey ?? 'preFetchCheckFailed',
          result.errorMessage
        );
        return false;
      }
    }

    return true;
  }

  // ============================================================
  // Error management
  // ============================================================

  private clearPipelineErrors(): void {
    // Clear NID verification errors
    this.deleteErrorOnFormControl(
      ENidVerificationErrorKeys.VERIFICATION_FAILED
    );
    this.deleteErrorOnFormControl(
      ENidVerificationErrorKeys.VERIFICATION_INVALID
    );
    this.deleteErrorOnFormControl(
      ENidVerificationErrorKeys.VERIFICATION_REQUIRED
    );

    // Clear pre-fetch check errors (dynamic keys from config)
    const preFetchChecks: IPreFetchCheckConfig[] =
      this.field.props?.['preFetchChecks'] ?? [];
    for (const check of preFetchChecks) {
      if (check.errorKey) {
        this.deleteErrorOnFormControl(check.errorKey);
      }
    }

    this.formControl.updateValueAndValidity();
    this.formStateService.clearGlobalError(this.field);
    this.statusClass = undefined;
  }

  private deleteErrorOnFormControl(errorKey: string): void {
    if (this.formControl.errors?.[errorKey]) {
      const errors = { ...this.formControl.errors };
      delete errors[errorKey];
      this.formControl.setErrors(
        Object.keys(errors).length > 0 ? errors : null
      );
    }
  }

  private setPipelineError(errorKey: string, message?: string): void {
    this.statusClass = GenericInputStatusEnum.DANGER;
    const errors: Record<string, { message: string | boolean }> = {
      ...this.formControl.errors,
      [errorKey]: { message: message ?? true },
    };
    this.formControl.setErrors(errors);
    this.formControl.updateValueAndValidity();

    if (message) {
      if (!this.field.validation) {
        this.field.validation = {};
      }
      if (!this.field.validation.messages) {
        this.field.validation.messages = {};
      }
      this.field.validation.messages[errorKey] = message;
    }

    if (this.field?.props?.['showGlobalError'] && message) {
      this.formStateService.addGlobalError(this.field, message);
    }

    this.cd.detectChanges();
  }

  // ============================================================
  // Main data fetch
  // ============================================================

  private executeMainDataFetch(inputValue: string) {
    if (this.isCurrentlyFetching) {
      return;
    }

    let { url } = this.field.props;
    const { endpointCode } = this.field.props;

    if (!url || !endpointCode) {
      throw new Error('url and endpointCode properties are required');
    }

    if (this.field.props['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    const payload = this.buildPayload(inputValue);
    const params: IIntegrationFetchSyncRequest = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    this.isCurrentlyFetching = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.formControl.setErrors(null);
    this.formControl.updateValueAndValidity();
    this.formStateService.clearGlobalError(this.field);
    this.subscriptions.add(
      this.http
        .post<IIntegrationFetchSyncResponse>(url, params)
        .pipe(
          finalize(() => {
            this.isCurrentlyFetching = false;
            if (
              this.field.props?.['triggerType'] ===
              EGenericInputTriggerType.TRIGGER_BUTTON
            ) {
              this.field.props['runAction'] = false;
            }
          })
        )
        .subscribe({
          next: (res: IIntegrationFetchSyncResponse) => {
            this.statusClass = GenericInputStatusEnum.SUCCESS;
            const response = res.data.response;

            this.formStateService.saveFetchedDataKeyInFormState(
              this.field,
              response
            );

            if (
              this.fieldsToPopulate.length > 0 &&
              this.field.formControl.valid
            ) {
              const transformConfig = this.field.props?.[
                'transformToArray'
              ] as TransformToArrayConfig;
              if (transformConfig?.enabled) {
                this.handleTransformToArray(response, transformConfig);
              }

              this.field.options?.detectChanges?.(this.field);

              populateFormFields(
                response,
                this.field,
                this.currentRepeaterForm,
                this.formStateService
              );
            } else {
              this.field.options?.detectChanges?.(this.field);
            }
            this.formControl.setErrors(null);
            this.field.formControl.updateValueAndValidity();
            this.cd.detectChanges();

            this.formStateService.emitFetchSuccess({
              fetchKey: this.field.key as string,
              componentId: this.componentId,
              response: res as unknown as Record<string, unknown>,
            });
          },
          error: error => {
            const { errors, statusClass } = handleErrorMapping(
              error?.error || error,
              this.field.props['errorMapping'],
              'invalidGenericInput',
              this.field,
              this.formControl,
              true
            );

            const errorKey = Object.keys(errors)[0];
            const errorMessage =
              errors[errorKey]?.message ||
              this.field.validation?.messages?.[errorKey];

            if (this.field?.props?.['showGlobalError'] && errorMessage)
              this.formStateService.addGlobalError(this.field, errorMessage);

            this.statusClass = statusClass;
            this.formControl.setErrors(errors);
            this.cd.detectChanges();
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ============================================================
  // Transform utilities
  // ============================================================

  private handleTransformToArray(
    response: string | object,
    config: TransformToArrayConfig
  ): void {
    try {
      let parsedResponse: Record<string, unknown>;
      if (typeof response === 'string') {
        parsedResponse = JSON.parse(response);
      } else {
        parsedResponse = response as Record<string, unknown>;
      }

      const sourceData = config.dataPath
        ? this.getNestedValue(parsedResponse, config.dataPath)
        : parsedResponse;

      if (!sourceData) {
        console.warn('transformToArray: No data found at specified path');
        return;
      }

      const transformedItem: Record<string, unknown> = {};
      config.mappings.forEach((mapping: TransformFieldMapping) => {
        const value = this.getNestedValue(sourceData, mapping.sourcePath);
        transformedItem[mapping.targetKey] = this.applyFormatter(
          value,
          mapping.formatter
        );
      });

      if (config.includeInputValues) {
        const inputValues: Record<string, unknown> = {};
        if (config.inputValueMappings?.length) {
          config.inputValueMappings.forEach(mapping => {
            inputValues[mapping.targetKey] = this.formControl.value;
          });
        } else {
          inputValues[this.field.key as string] = this.formControl.value;
        }
        transformedItem['__inputValues'] = inputValues;
      }

      const resultArray = [transformedItem];
      const targetControl = this.findTargetControl(config.targetField);

      if (targetControl) {
        targetControl.setValue(resultArray);
        targetControl.updateValueAndValidity();
        this.saveTransformDataToFormState(config, resultArray);
      } else {
        console.warn(
          `transformToArray: Target field '${config.targetField}' not found`
        );
      }
    } catch (error) {
      console.error('transformToArray: Error transforming data', error);
    }
  }

  private findTargetControl(targetKey: string): AbstractControl | null {
    const targetField = this.findTargetFormlyField(targetKey);
    if (targetField?.formControl) return targetField.formControl;

    const currentFormControl = this.form.get(targetKey);
    if (currentFormControl) return currentFormControl;

    if (this.field.parent?.formControl) {
      const parentControl = (
        this.field.parent.formControl as AbstractControl
      ).get?.(targetKey);
      if (parentControl) return parentControl;
    }

    let rootForm = this.form;
    while (rootForm.parent) {
      rootForm = rootForm.parent;
    }
    return findFormControl(rootForm, targetKey);
  }

  private saveTransformDataToFormState(
    config: TransformToArrayConfig,
    resultArray: Record<string, unknown>[]
  ): void {
    const targetField = this.findTargetFormlyField(config.targetField);
    if (targetField) {
      if (config.columns?.length) {
        targetField.props = targetField.props || {};
        targetField.props['columns'] = config.columns;
      }
      if (config.previewMetadata?.length) {
        targetField.props = targetField.props || {};
        targetField.props['previewMetadata'] = config.previewMetadata;
      }
      this.formStateService.saveFetchedDataKeyInFormState(
        targetField,
        resultArray,
        EIremboFormlyFormStateKeys.FETCHED_DATA,
        false
      );
      if (config.columns?.length) {
        this.formStateService.saveFetchedDataKeyInFormState(
          targetField,
          config.columns,
          EIremboFormlyFormStateKeys.FETCHED_COLUMNS_DATA,
          false
        );
      }
      if (config.previewMetadata?.length) {
        const metadata = this.computeTransformPreviewMetadata(
          config,
          resultArray
        );
        this.formStateService.saveFetchedDataKeyInFormState(
          targetField,
          metadata,
          EIremboFormlyFormStateKeys.FETCHED_METADATA,
          false
        );
      }
    }
  }

  private computeTransformPreviewMetadata(
    config: TransformToArrayConfig,
    resultArray: Record<string, unknown>[]
  ): Record<string, unknown> {
    const metadata: Record<string, unknown> = {};
    config.previewMetadata?.forEach(metaConfig => {
      const sourceField = metaConfig.sourceField || '';
      switch (metaConfig.type) {
        case 'count':
          metadata[metaConfig.key] = resultArray.length;
          break;
        case 'sum':
          metadata[metaConfig.key] = resultArray.reduce((sum, item) => {
            return sum + (Number(this.getNestedValue(item, sourceField)) || 0);
          }, 0);
          break;
        case 'first':
          metadata[metaConfig.key] =
            resultArray.length > 0
              ? this.getNestedValue(resultArray[0], sourceField)
              : null;
          break;
        case 'input':
          metadata[metaConfig.key] = this.formControl.value || null;
          break;
        case 'static':
          metadata[metaConfig.key] = metaConfig.value;
          break;
      }
    });
    return metadata;
  }

  private findTargetFormlyField(targetKey: string): FormlyFieldConfig | null {
    const searchField = (
      fields: FormlyFieldConfig[]
    ): FormlyFieldConfig | null => {
      for (const field of fields) {
        if (field.key === targetKey) return field;
        if (field.fieldGroup?.length) {
          const found = searchField(field.fieldGroup);
          if (found) return found;
        }
      }
      return null;
    };

    let rootField = this.field as FormlyFieldConfig;
    while (rootField.parent) {
      rootField = rootField.parent as FormlyFieldConfig;
    }
    if (rootField.fieldGroup) {
      return searchField(rootField.fieldGroup);
    }
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getNestedValue(obj: any, path: string): unknown {
    if (!obj || !path) return obj;
    return path
      .split('.')
      .reduce((current: any, prop: string) => current?.[prop], obj);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private applyFormatter(value: any, formatter?: string): unknown {
    if (!formatter || value === null || value === undefined) return value;
    switch (formatter) {
      case 'date':
        return this.formatDate(value);
      case 'currency':
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      case 'array-first-name':
        return Array.isArray(value) && value.length > 0
          ? value[0]?.name
          : 'N/A';
      default:
        return value;
    }
  }

  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date
        .getDate()
        .toString()
        .padStart(2, '0')}/${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  }
}
